import type { Entry, UnresolvedLink } from 'contentful';
import type { Cta, InternalPage } from 'libs/contentful/types';
import type { CtaInternalPageSkeleton, CtaSkeleton } from './types';

function toInternalPage(
  page: Entry<CtaInternalPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Entry'> | undefined,
): InternalPage | undefined {
  // A link to an unpublished/archived page survives as a raw Link ref (no `fields`),
  // not undefined — the `WITHOUT_UNRESOLVABLE_LINKS` field type understates this, so
  // the structural `'fields' in page` check is what actually drops it.
  if (page == null || !('fields' in page)) {
    return undefined;
  }

  return { sys: { id: page.sys.id }, slug: page.fields.slug };
}

/**
 * Flattens a resolved Contentful `cta` entry (the raw Entry-native shape) into the
 * flat `Cta` that the shared `<CTA>` renderer consumes. Entry-native modules that
 * pass a cta entry to `<CTA>` should route it through this.
 *
 * This is the migration seam between the raw Entry-native `cta` (`CtaSkeleton`) and
 * the flat `Cta` render contract that all `<CTA>` consumers share, so it must accept
 * every `cta` shape reachable in production:
 *
 * - Resolved entry (`{ fields }`): the fresh Entry-native shape — flatten it.
 * - Flat `Cta` (`text`/`url` at the top level, no `fields`): a page cached by the
 *   pre-migration adapter and served to the new renderer through a rolling deploy.
 *   Pass it through so the CTA still renders instead of being silently dropped.
 * - Unresolvable link (a raw Link ref: no `fields`, no `text`): the REST client's
 *   default chain leaves links to archived/unpublished entries as raw Link objects
 *   rather than removing them, so `fields` is not guaranteed at runtime — drop it.
 *   Mirrors components/shared/Media/toMediaAsset.ts.
 */
export function toCta(
  cta: Entry<CtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Entry'> | Cta | undefined,
): Cta | undefined {
  if (cta == null) {
    return undefined;
  }

  if ('fields' in cta) {
    const { fields, sys } = cta;

    return {
      sys: { id: sys.id },
      text: fields.text,
      url: fields.url,
      internalPage: toInternalPage(fields.internalPage),
      target: fields.target,
      variant: fields.variant,
    };
  }

  // Already-flat stale-cache Cta — a required `text` distinguishes it from a raw Link.
  if ('text' in cta && typeof cta.text === 'string') {
    return cta;
  }

  return undefined;
}
